// ═══════════════════════════════════════════════════════════
// Well-Known Formula Variables
// Auto-resolved from CRM entity fields — no manual mapping needed
// Used by both frontend FormulaEditor autocomplete and backend
// formula.service.ts auto-injection
// ═══════════════════════════════════════════════════════════
import type { FormulaVariable } from "../types/formula";

export interface WellKnownVariableDef extends FormulaVariable {
  /** Display label shown in the formula editor autocomplete */
  label: string;
  /** Example value shown in the editor preview */
  sampleValue: number;
  /** Human-readable description of the variable */
  description: string;
  /**
   * For source === "METRIC" variables: the D1 journal MetricKey to query.
   * "REV" = cumulative base_amount (revenue), "CNT" = deal count, "COM" = earned commissions (APPROVED+PAID only)
   */
  metricKey?: "REV" | "CNT" | "COM";
  /**
   * For source === "METRIC" variables: the period type used to compute the
   * current period label (e.g. "MONTHLY" → "2026-04").
   */
  metricPeriod?: "MONTHLY" | "QUARTERLY" | "SEMI_ANNUAL" | "ANNUAL";
}

/**
 * Canonical set of formula variables that are automatically resolved from
 * CRM entity fields without requiring any manual binding by the user.
 *
 * Key = the variable name as it appears in formula expressions: {deal_amount}
 */
export const WELL_KNOWN_VARIABLES: Record<string, WellKnownVariableDef> = {
  deal_amount: {
    label: "Entity Amount",
    description:
      "Total entity opportunity value (OPPORTUNITY field — available on all commission entities)",
    source: "ENTITY",
    field: "opportunity",
    type: "currency",
    defaultValue: 0,
    sampleValue: 10000,
  },
  product_price: {
    label: "Product Price",
    description: "Unit price of the current product row",
    source: "PRODUCT_ROW",
    field: "price",
    type: "currency",
    defaultValue: 0,
    sampleValue: 500,
  },
  product_qty: {
    label: "Product Quantity",
    description: "Quantity of units in the current product row",
    source: "PRODUCT_ROW",
    field: "quantity",
    type: "number",
    defaultValue: 1,
    sampleValue: 5,
  },
  product_discount: {
    label: "Product Discount %",
    description: "Discount percentage applied to the product row",
    source: "PRODUCT_ROW",
    field: "discountRate",
    type: "number",
    defaultValue: 0,
    sampleValue: 10,
  },
  probability: {
    label: "Win Probability %",
    description: "Win probability percentage (available on deals, quotes, and leads)",
    source: "ENTITY",
    field: "probability",
    type: "number",
    defaultValue: 0,
    sampleValue: 80,
  },

  // ─── Per-Row Variables (ITERATION mode) ───────────────
  parent_product_id: {
    label: "Catalog Product ID",
    description:
      "Bitrix24 catalog product ID linked to this line item (PRODUCT_ID field on the product row)",
    source: "PRODUCT_ROW",
    field: "productId",
    type: "number",
    defaultValue: 0,
    sampleValue: 101,
  },
  price: {
    label: "Product Price",
    description: "Final unit price — after discount, tax-included if applicable",
    source: "PRODUCT_ROW",
    field: "price",
    type: "currency",
    defaultValue: 0,
    sampleValue: 99,
  },
  quantity: {
    label: "Quantity",
    description: "Number of units ordered in this line item",
    source: "PRODUCT_ROW",
    field: "quantity",
    type: "number",
    defaultValue: 0,
    sampleValue: 3,
  },
  lineTotal: {
    label: "Line Total",
    description: "price × quantity for this line item",
    source: "COMPUTED",
    field: "lineTotal",
    type: "currency",
    defaultValue: 0,
    sampleValue: 297,
  },
  discount: {
    label: "Discount Per Unit",
    description: "Discount amount per unit (discountSum)",
    source: "PRODUCT_ROW",
    field: "discountSum",
    type: "currency",
    defaultValue: 0,
    sampleValue: 10,
  },
  discountTotal: {
    label: "Line Discount Total",
    description: "discountSum × quantity for this line item",
    source: "COMPUTED",
    field: "discountTotal",
    type: "currency",
    defaultValue: 0,
    sampleValue: 30,
  },
  taxRate: {
    label: "Tax Rate %",
    description: "Tax percentage on this line item (null → 0)",
    source: "PRODUCT_ROW",
    field: "taxRate",
    type: "number",
    defaultValue: 0,
    sampleValue: 10,
  },
  priceBeforeDiscount: {
    label: "List Price (No Discount/Tax)",
    description: "Unit price before discount and tax (priceNetto)",
    source: "PRODUCT_ROW",
    field: "priceNetto",
    type: "currency",
    defaultValue: 0,
    sampleValue: 100,
  },
  priceWithTax: {
    label: "List Price + Tax",
    description: "Unit price with tax, before discount (priceBrutto)",
    source: "PRODUCT_ROW",
    field: "priceBrutto",
    type: "currency",
    defaultValue: 0,
    sampleValue: 110,
  },
  priceAfterDiscount: {
    label: "Net Price (After Discount)",
    description: "Unit price after discount, before tax (priceExclusive)",
    source: "PRODUCT_ROW",
    field: "priceExclusive",
    type: "currency",
    defaultValue: 0,
    sampleValue: 90,
  },

  // ─── Aggregate Variables (Both SINGLE & ITERATION) ────
  totalAmount: {
    label: "Total Sales Amount",
    description: "Sum of (price × quantity) across all product rows",
    source: "COMPUTED",
    field: "totalAmount",
    type: "currency",
    defaultValue: 0,
    sampleValue: 50000,
  },
  net_amount: {
    label: "Net Revenue (Product Rows)",
    description:
      "Sum of net product row amounts (final price × qty after discounts) — alias for Total Sales Amount",
    source: "COMPUTED",
    field: "totalAmount",
    type: "currency",
    defaultValue: 0,
    sampleValue: 50000,
  },
  totalBeforeTax: {
    label: "Total Before Tax",
    description: "Sum of (priceExclusive × quantity) across all rows",
    source: "COMPUTED",
    field: "totalBeforeTax",
    type: "currency",
    defaultValue: 0,
    sampleValue: 45000,
  },
  totalListPrice: {
    label: "Total at List Price",
    description: "Sum of (priceNetto × quantity) across all rows",
    source: "COMPUTED",
    field: "totalListPrice",
    type: "currency",
    defaultValue: 0,
    sampleValue: 55000,
  },
  totalDiscount: {
    label: "Total Discount",
    description: "Sum of (discountSum × quantity) across all rows",
    source: "COMPUTED",
    field: "totalDiscount",
    type: "currency",
    defaultValue: 0,
    sampleValue: 5000,
  },
  totalTax: {
    label: "Total Tax",
    description: "Sum of tax amounts across all rows",
    source: "COMPUTED",
    field: "totalTax",
    type: "currency",
    defaultValue: 0,
    sampleValue: 4500,
  },
  totalQuantity: {
    label: "Total Quantity",
    description: "Sum of quantities across all product rows",
    source: "COMPUTED",
    field: "totalQuantity",
    type: "number",
    defaultValue: 0,
    sampleValue: 25,
  },
  lineItemCount: {
    label: "Line Item Count",
    description: "Number of product line items",
    source: "COMPUTED",
    field: "lineItemCount",
    type: "number",
    defaultValue: 0,
    sampleValue: 5,
  },

  // ─── Progress Metric Variables (METRIC source — cmn_totals) ─
  // These are pre-resolved from the rep's cumulative totals in cmn_totals
  // before formula evaluation. All use ruleId=0 (all-rules aggregate).
  cumul_rev_month: {
    label: "Cumulative Revenue (Month)",
    description: "Rep's total closed revenue in the current calendar month (all rules)",
    source: "METRIC",
    field: "cumul_rev_month",
    type: "currency",
    defaultValue: 0,
    sampleValue: 75000,
    metricKey: "REV",
    metricPeriod: "MONTHLY",
  },
  cumul_rev_quarter: {
    label: "Cumulative Revenue (Quarter)",
    description: "Rep's total closed revenue in the current calendar quarter (all rules)",
    source: "METRIC",
    field: "cumul_rev_quarter",
    type: "currency",
    defaultValue: 0,
    sampleValue: 210000,
    metricKey: "REV",
    metricPeriod: "QUARTERLY",
  },
  cumul_rev_year: {
    label: "Cumulative Revenue (Year)",
    description: "Rep's total closed revenue in the current calendar year (all rules)",
    source: "METRIC",
    field: "cumul_rev_year",
    type: "currency",
    defaultValue: 0,
    sampleValue: 750000,
    metricKey: "REV",
    metricPeriod: "ANNUAL",
  },
  deal_count_month: {
    label: "Deal Count (Month)",
    description: "Number of deals closed by this rep in the current calendar month (all rules)",
    source: "METRIC",
    field: "deal_count_month",
    type: "number",
    defaultValue: 0,
    sampleValue: 8,
    metricKey: "CNT",
    metricPeriod: "MONTHLY",
  },
  deal_count_quarter: {
    label: "Deal Count (Quarter)",
    description: "Number of deals closed by this rep in the current calendar quarter (all rules)",
    source: "METRIC",
    field: "deal_count_quarter",
    type: "number",
    defaultValue: 0,
    sampleValue: 22,
    metricKey: "CNT",
    metricPeriod: "QUARTERLY",
  },
  deal_count_year: {
    label: "Deal Count (Year)",
    description: "Number of deals closed by this rep in the current calendar year (all rules)",
    source: "METRIC",
    field: "deal_count_year",
    type: "number",
    defaultValue: 0,
    sampleValue: 85,
    metricKey: "CNT",
    metricPeriod: "ANNUAL",
  },
  earned_com_month: {
    label: "Earned Commissions (Month)",
    description: "Total commissions earned by this rep in the current calendar month (all rules)",
    source: "METRIC",
    field: "earned_com_month",
    type: "currency",
    defaultValue: 0,
    sampleValue: 3200,
    metricKey: "COM",
    metricPeriod: "MONTHLY",
  },
  earned_com_quarter: {
    label: "Earned Commissions (Quarter)",
    description: "Total commissions earned by this rep in the current calendar quarter (all rules)",
    source: "METRIC",
    field: "earned_com_quarter",
    type: "currency",
    defaultValue: 0,
    sampleValue: 9500,
    metricKey: "COM",
    metricPeriod: "QUARTERLY",
  },
} as const;

/** Array form of WELL_KNOWN_VARIABLES for use in UI lists */
export const WELL_KNOWN_VARIABLES_LIST = Object.entries(WELL_KNOWN_VARIABLES).map(
  ([name, def]) => ({ name, ...def }),
);
